import type { Actor } from '@w2w-dx/contracts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, api } from '@/lib/api-client';

/**
 * Who is signed in, and whether they are allowed in at all.
 *
 * Membership of the `ways2well` org is the access gate, and it is checked
 * server-side; this store only reflects the answer. The two rejections are
 * kept apart because they need different words on screen: "sign in" is a
 * prompt, "you are not a member of this org" is an explanation.
 */
export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous' | 'forbidden';

export const useAuthStore = defineStore('auth', () => {
  const status = ref<AuthStatus>('unknown');
  const viewer = ref<Actor | null>(null);
  const orgVerifiedAt = ref<string | null>(null);
  const error = ref<string | null>(null);

  let inFlight: Promise<void> | null = null;

  const isAuthenticated = computed(() => status.value === 'authenticated');
  const isResolved = computed(() => status.value !== 'unknown');

  function applyRejection(cause: unknown): void {
    if (cause instanceof ApiError && cause.status === 403) {
      status.value = 'forbidden';
      error.value = cause.message;
      return;
    }

    if (cause instanceof ApiError && cause.status === 401) {
      status.value = 'anonymous';
      error.value = null;
      return;
    }

    // A network failure is not the same as being signed out, and treating it
    // as one would throw somebody back to the sign-in screen every time their
    // connection hiccups.
    status.value = 'unknown';
    error.value = cause instanceof Error ? cause.message : 'Could not check your session.';
  }

  /**
   * Resolves the session once at start-up. `quiet` is set so a 401 here does
   * not trip the client's global redirect: this call is how the shell finds
   * out there is no session, and bouncing mid-resolution would be circular.
   */
  function resolve(): Promise<void> {
    if (inFlight) return inFlight;

    inFlight = api
      .getMe({ quiet: true })
      .then((me) => {
        viewer.value = me.viewer;
        orgVerifiedAt.value = me.orgVerifiedAt;
        status.value = 'authenticated';
        error.value = null;
      })
      .catch((cause: unknown) => {
        viewer.value = null;
        orgVerifiedAt.value = null;
        applyRejection(cause);
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  /** Marks the session dead locally. Called when the API returns a 401 later on. */
  function markSignedOut(): void {
    viewer.value = null;
    orgVerifiedAt.value = null;
    status.value = 'anonymous';
  }

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } catch {
      // The cookie may already be gone. Either way the local state should end
      // up signed out, so the failure is not worth surfacing.
    }
    markSignedOut();
  }

  /** Full-page navigation target for the sign-in button. */
  function signInUrl(returnTo?: string): string {
    return api.signInUrl(returnTo);
  }

  return {
    status,
    viewer,
    orgVerifiedAt,
    error,
    isAuthenticated,
    isResolved,
    resolve,
    markSignedOut,
    logout,
    signInUrl,
  };
});
